import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';

export default function CTA() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="cta" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />

      {/* Animated background */}
      <div className="absolute inset-0 navy-gradient" />
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.08, 0.16, 0.08] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
        style={{ background: 'radial-gradient(circle, #d4950e 0%, transparent 70%)' }}
      />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-3xl p-10 md:p-16 text-center border border-gold-600/15 shadow-2xl">
          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 rounded-2xl gold-gradient flex items-center justify-center mx-auto mb-8 shadow-xl"
          >
            <Calendar size={30} className="text-navy-950" strokeWidth={1.8} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
          >
            Diskusikan Permasalahan{' '}
            <span className="gold-text">Hukum Anda</span>{' '}
            Sekarang
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-navy-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Jangan biarkan masalah hukum menghambat Anda. Tim advokat kami siap
            memberikan solusi terbaik untuk setiap permasalahan Anda.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.a
              href="#kontak"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="btn-primary text-base justify-center"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#kontak')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Calendar size={20} />
              Jadwalkan Konsultasi
              <ArrowRight size={18} />
            </motion.a>
            <motion.a
              href="https://wa.me/6281234567890"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="btn-outline text-base justify-center"
            >
              WhatsApp Kami
            </motion.a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6 mt-10 pt-8 border-t border-white/5"
          >
            {['Konsultasi Rahasia', 'Respons < 24 Jam', 'Tim Berpengalaman'].map((t) => (
              <div key={t} className="flex items-center gap-2 text-navy-300 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-400 flex-shrink-0" />
                {t}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
